import { Sheet } from '@sunwebgroup/uds-ui/sheet';
import React from 'react';
import { useIntl } from 'react-intl';
import { CheckoutParticipant, OverviewService } from '../../domain/types';
import styles from './TravelerDetailsSheet.module.css';
import { Typography } from '@sunwebgroup/uds-ui/typography';
import { formatParticipantFullName, getExtraParticipantServices } from './travelerHelper';
import { getParticipantTitleTextKey } from '../../domain/participantTitleText';

interface TravelersDetailsSheetProps {
  isOpen: boolean;
  participant: CheckoutParticipant | null;
  services: OverviewService[];
  setIsOpen: (isOpen: boolean) => void;
}
    
interface TravelerDetailFieldProps {
  label: string;
  values: string[];
}

function TravelerDetailField({ label, values }: TravelerDetailFieldProps) {

  if (values.length === 0) {
    return null
  }

  return (
    <div className={styles.fieldSection}>
      <Typography className={styles.fieldSectionTitle} as="div" variant="body-s-regular-tight">
        {label}
      </Typography>
      {/* eslint-disable-next-line react/no-array-index-key */}
      {values.map((value, index) => <Typography key={`${value}-${index}`} className={styles.fieldSectionValue} as="div">{value}</Typography>)}
    </div>
  )
}

export function TravelerDetailsSheet({ isOpen, participant, services, setIsOpen }: TravelersDetailsSheetProps) {
  const intl = useIntl();
  if (!participant) {
    return null;
  }
  const titleKey = getParticipantTitleTextKey(participant.title);
  const extras = getExtraParticipantServices(services, participant.id);
  return (
    <Sheet.Root open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <Sheet.Body side="right">
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'common_participant' })}
        />
        <Sheet.Content className={styles.sheetContent}>
          <Typography className={styles.sheetTitle} as="div" variant="display-g-regular">
            {participant && formatParticipantFullName(
              titleKey ? intl.formatMessage({ id: titleKey }) : null,
              participant.name,
              participant.surname,
            )}
          </Typography>
          <TravelerDetailField
            label={intl.formatMessage({ id: 'common_birthdate_label' })}
            values={participant.birthdate ? [participant.birthdate] : []}
          />
          <TravelerDetailField
            label={intl.formatMessage({ id: 'common_address' })}
            values={participant.address ? [participant.address] : []}
          />
          <TravelerDetailField
            label={intl.formatMessage({ id: 'email_address' })}
            values={participant.email ? [participant.email] : []}
          />
          {participant.mobilePhoneNumber && (
            <TravelerDetailField
              label={intl.formatMessage({ id: 'common_phone' })}
              values={[`+${participant.mobilePhoneNumber.countryCode}${participant.mobilePhoneNumber.phoneNumber}`]}
            />
          )}
          {participant.emergencyPhoneNumber && (
            <TravelerDetailField
              label={intl.formatMessage({ id: 'common_emergency_phone' })}
              values={[`+${participant.emergencyPhoneNumber.countryCode}${participant.emergencyPhoneNumber.phoneNumber}`]}
            />
          )}
          {(extras.length > 0) && (
            <>
              <Typography className={styles.sectionTitle} as="div" variant="body-m-bold-tight">
                {intl.formatMessage({ id: 'common_extras' })}
              </Typography>
              {extras.map(extra => (
                <TravelerDetailField
                  key={extra.title}
                  label={extra.title}
                  values={extra.lines.map(line => line.description ?? '')}
                />
              ))}
            </>
          )}
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  );
}

export default TravelerDetailsSheet;
